'use client'

import { memo } from 'react'
import Link from 'next/link'
import {
  Building2,
  CloudOff,
  Cpu,
  FileSearch,
  Gavel,
  HeartPulse,
  Landmark,
  Lock,
  Route,
  Scale,
  ScrollText,
  Server,
  ShieldCheck,
  Wallet,
  Workflow,
} from 'lucide-react'
import { NinxlSectionHeader } from './ninxl-graphics'
import { Ninxl8BitHeroChips, NinxlEnterpriseHeroChipVisual } from './ninxl-8bit-processor-chip'
import {
  NinxlEnterpriseRouterDiagram,
  NinxlEnterpriseSectionBackdrop,
  NinxlEnterpriseWorkflowPipeline,
} from './ninxl-enterprise-graphics'
import { NinxlEnterpriseComparisonTable } from './ninxl-enterprise-comparison-table'
import { NinxlScrollReveal, NinxlStaggerReveal, NinxlStaggerItem } from './ninxl-scroll-reveal'
import { NinxlWordRotator } from './ninxl-scroll-motion'
import { Ninxl8BitDivider, Ninxl8BitFrame, Ninxl8BitSection } from './ninxl-8bit-ui'
import { NinxlEvervaultShell } from './ninxl-evervault-shell'
import { NinConnectPlatformSection } from '@/app/(marketing)/_components/nin-connect-platform-section'

export const NINX_ENTERPRISE_SALES_EMAIL = 'enterprise@nin.in'

export const PARADOX_RISKS = [
  {
    title: 'Data Privacy Risks',
    desc: 'Sensitive financials, source code, and records exposed to third-party clouds.',
    icon: CloudOff,
    accent: 'ninxl-enterprise-accent-rose',
  },
  {
    title: 'Compliance Barriers',
    desc: 'Strict regulatory requirements often prohibit cloud-based AI processing.',
    icon: Gavel,
    accent: 'ninxl-enterprise-accent-amber',
  },
  {
    title: 'Spiraling Costs',
    desc: 'Unpredictable and rising cloud consumption fees.',
    icon: Wallet,
    accent: 'ninxl-enterprise-accent-orange',
  },
  {
    title: 'Fragmented Workflows',
    desc: 'AI tools that lack deep integration with internal systems.',
    icon: Workflow,
    accent: 'ninxl-enterprise-accent-violet',
  },
] as const

export const WORKFLOW_STEPS = [
  { step: 'Prompt', detail: '"Review this week\'s Project Alpha emails..."' },
  { step: 'Extract', detail: 'Pulls data from Email Client.' },
  { step: 'Compare', detail: 'Checks data against Proposal Documents.' },
  { step: 'Draft', detail: 'Creates a response in Word Processor.' },
  { step: 'Schedule', detail: 'Books time in Calendar.' },
  { step: 'Collaborate', detail: 'Pings the team in your Collaboration Tool.' },
] as const

const HERO_STATS = [
  { value: '120B', label: 'Pocket Node models' },
  { value: 'Zero', label: 'mandatory cloud calls' },
  { value: '100%', label: 'audit-ready actions' },
] as const

const ROUTE_CARDS = [
  {
    title: 'Sensitive Data',
    desc: 'Handled by a Local / On-Device Engine: data stays on your systems.',
    icon: Lock,
    accent: 'ninxl-enterprise-route-card',
    featured: true,
  },
  {
    title: 'Complex Logic',
    desc: 'Optional cloud credits for frontier reasoning under Zero Data Retention (ZDR).',
    icon: Route,
    accent: 'ninxl-enterprise-route-card ninxl-enterprise-route-card--sky',
  },
] as const

const HARDWARE_FEATURES = [
  {
    title: 'Physical AI Appliances',
    desc: 'A persistent execution layer where software and hardware speak the same native language for zero-latency performance.',
    icon: Server,
    stat: '0ms',
    statLabel: 'edge latency target',
  },
  {
    title: 'The Pocket Node',
    desc: 'A secure, pocket-sized persistent node capable of running 120B parameter models at real-time speed, even in air-gapped environments.',
    icon: Cpu,
    stat: '120B',
    statLabel: 'on-device params',
  },
] as const

const GOVERNANCE_FEATURES = [
  { title: 'CASA Certified', desc: 'Independent security audit for desktop apps.', icon: ShieldCheck },
  { title: 'SOC 2 in Progress', desc: 'Type II audit underway for security and availability controls.', icon: Lock },
  {
    title: 'ZDR Policy',
    desc: 'On-device work stays on your systems. Optional cloud credits use Zero Data Retention.',
    icon: CloudOff,
  },
  { title: 'Audit Logs', desc: 'Maintain a complete history of AI actions for compliance.', icon: ScrollText },
] as const

const REGULATED_INDUSTRIES = [
  { title: 'Government', desc: 'Processing citizen records with zero external transmission.', icon: Landmark, accent: 'ninxl-enterprise-accent-sky' },
  { title: 'Legal', desc: 'Ingesting thousands of confidential discovery documents locally.', icon: Scale, accent: 'ninxl-enterprise-accent-violet' },
  { title: 'Banking & Insurance', desc: 'Executing workflows across sensitive financial systems.', icon: Wallet, accent: 'ninxl-enterprise-accent-emerald' },
  { title: 'Healthcare', desc: 'Managing patient data within a secure local perimeter.', icon: HeartPulse, accent: 'ninxl-enterprise-accent-rose' },
] as const

const COMMERCIAL_MODEL = [
  { title: 'Enterprise Licensing', desc: 'Flexible per-user subscriptions.' },
  { title: 'Deployment Services', desc: 'Professional migration and integration support.' },
  { title: 'Enterprise Support', desc: 'Priority security updates and dedicated customer success.' },
] as const

export const NinxlEnterpriseSection = memo(function NinxlEnterpriseSection() {
  return (
    <>
      {/* HERO */}
      <section data-section="enterprise-hero" className="relative pt-14 pb-16 md:pt-20 md:pb-20">
        <div className="mx-auto max-w-5xl px-6 text-center">
          <NinxlScrollReveal variant="blur">
            <Ninxl8BitHeroChips className="mb-6" />

            <h1 className="ninxl-8bit-heading ninxl-theme-text mx-auto mb-6 max-w-4xl text-lg leading-relaxed sm:text-xl md:text-2xl">
              Sovereign AI Infrastructure.
              <br />
              <span className="ninxl-theme-text-muted inline-flex flex-wrap items-baseline justify-center gap-x-2">
                <span>Built</span>
                <NinxlWordRotator
                  words={['private by default.', 'hybrid by design.', 'autonomous at scale.']}
                  className="ninxl-theme-text-accent"
                />
              </span>
            </h1>

            <p className="ninxl-8bit-body mx-auto mb-8 max-w-2xl text-base md:text-lg">
              Empower your organization with intelligent systems that act securely on behalf of every
              employee: without surrendering control of sensitive data.
            </p>

            <div className="mb-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href={`mailto:${NINX_ENTERPRISE_SALES_EMAIL}?subject=NINX%20Enterprise%20Pilot%20Inquiry`}
                className="ninxl-pixel-btn ninxl-pixel-btn-primary"
              >
                Contact Sales
              </Link>
              <a href="#enterprise-advantage" className="ninxl-pixel-btn">
                See the advantage
              </a>
            </div>
          </NinxlScrollReveal>

          <NinxlScrollReveal delay={0.12} variant="scale">
            <NinxlEnterpriseHeroChipVisual />
          </NinxlScrollReveal>

          <NinxlStaggerReveal className="mt-10 grid max-w-3xl grid-cols-3 gap-3 mx-auto" stagger={0.08}>
            {HERO_STATS.map((stat) => (
              <NinxlStaggerItem key={stat.label}>
                <div className="ninxl-hud-panel px-3 py-4 text-center">
                  <div className="ninxl-theme-text-accent text-xl font-bold md:text-2xl">{stat.value}</div>
                  <div className="ninxl-8bit-label mt-1 text-[9px] md:text-[10px]">{stat.label}</div>
                </div>
              </NinxlStaggerItem>
            ))}
          </NinxlStaggerReveal>
        </div>
      </section>

      <Ninxl8BitDivider />

      {/* PARADOX */}
      <Ninxl8BitSection data-section="paradox" className="relative overflow-hidden">
        <NinxlEnterpriseSectionBackdrop variant="paradox" />
        <div className="mx-auto max-w-6xl px-6">
          <NinxlScrollReveal>
            <NinxlSectionHeader
              label="THE DILEMMA"
              title="The AI Adoption Paradox"
              description="Organizations want AI productivity, but cloud-first tools force a dangerous compromise."
            />
          </NinxlScrollReveal>

          <NinxlStaggerReveal className="grid gap-4 md:grid-cols-2" stagger={0.1}>
            {PARADOX_RISKS.map((risk) => (
              <NinxlStaggerItem key={risk.title}>
                <div
                  className={`ninxl-enterprise-card group relative overflow-hidden rounded-3xl border p-6 transition duration-300 hover:-translate-y-1 hover:shadow-xl ${risk.accent}`}
                >
                  <div className="ninxl-card-glint" />
                  <div className="relative">
                    <div className="ninxl-enterprise-icon mb-4 inline-flex rounded-2xl p-2.5 transition duration-300 group-hover:scale-110">
                      <risk.icon className="h-5 w-5" />
                    </div>
                    <h3 className="ninxl-theme-text mb-2 text-lg font-semibold">{risk.title}</h3>
                    <p className="ninxl-theme-text-muted text-sm leading-relaxed">{risk.desc}</p>
                  </div>
                </div>
              </NinxlStaggerItem>
            ))}
          </NinxlStaggerReveal>
        </div>
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      {/* HYBRID */}
      <Ninxl8BitSection alt data-section="hybrid" className="relative overflow-hidden">
        <NinxlEnterpriseSectionBackdrop variant="hybrid" />
        <div className="mx-auto max-w-6xl px-6">
          <NinxlScrollReveal>
            <NinxlSectionHeader
              label="HYBRID CONTROL"
              title="A paradigm for uncompromised control"
              description="NINX combines frontier reasoning with the security of local deployment."
            />
          </NinxlScrollReveal>

          <div className="grid items-center gap-10 lg:grid-cols-2">
            <NinxlScrollReveal variant="left">
              <NinxlEnterpriseRouterDiagram />
            </NinxlScrollReveal>

            <NinxlScrollReveal variant="right" delay={0.1}>
              <Ninxl8BitFrame label="PRIVACY GATE" flush>
                <NinxlEvervaultShell contentClassName="p-6 md:p-8 text-left">
                  <h3 className="ninxl-theme-text mb-3 text-base font-bold">The Hybrid Intelligence Router</h3>
                  <p className="ninxl-8bit-body mb-5">
                    Our proprietary <strong>Privacy Gate</strong> routes by sensitivity: on-device
                    stays local; cloud credits use <strong>ZDR</strong> (Zero Data Retention).
                  </p>
                  <div className="space-y-3">
                    {ROUTE_CARDS.map((card) => (
                      <div
                        key={card.title}
                        className={`rounded-2xl border p-4 ${card.accent}`}
                      >
                        <div className="mb-2 flex items-center gap-2">
                          <card.icon className="ninxl-theme-text-accent h-4 w-4" />
                          <span className="ninxl-theme-text font-semibold">{card.title}</span>
                        </div>
                        <p className="ninxl-theme-text-muted text-sm">{card.desc}</p>
                      </div>
                    ))}
                  </div>
                </NinxlEvervaultShell>
              </Ninxl8BitFrame>
            </NinxlScrollReveal>
          </div>
        </div>
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      {/* WORKFLOWS */}
      <Ninxl8BitSection data-section="workflows" className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,rgba(33,114,141,0.06),transparent)]" />
        <div className="mx-auto max-w-6xl px-6">
          <NinxlScrollReveal>
            <NinxlSectionHeader
              label="AGENTIC EXECUTION"
              title="Beyond chat: executing real work"
              description="Multi-step workflows across Email, ERP, and CRM: inside a secure local perimeter."
            />
          </NinxlScrollReveal>

          <NinxlScrollReveal delay={0.1}>
            <Ninxl8BitFrame label="EXAMPLE WORKFLOW">
              <NinxlEnterpriseWorkflowPipeline steps={WORKFLOW_STEPS} />
              <p className="ninxl-8bit-label ninxl-theme-text-muted mt-6 text-center">
                All steps run inside a Secure Local Execution Perimeter.
              </p>
            </Ninxl8BitFrame>
          </NinxlScrollReveal>
        </div>
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      {/* ADVANTAGE */}
      <Ninxl8BitSection alt id="enterprise-advantage" data-section="advantage">
        <div className="mx-auto max-w-5xl px-6">
          <NinxlScrollReveal>
            <NinxlSectionHeader
              label="WHY NINX"
              title="The Enterprise Advantage"
              description="Cloud wrappers and local-only LLMs each solve half the problem. NINX solves both."
            />
          </NinxlScrollReveal>
          <NinxlScrollReveal delay={0.12}>
            <NinxlEnterpriseComparisonTable />
          </NinxlScrollReveal>
        </div>
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      {/* HARDWARE */}
      <Ninxl8BitSection data-section="hardware" className="relative overflow-hidden">
        <NinxlEnterpriseSectionBackdrop variant="hardware" />
        <div className="mx-auto max-w-5xl px-6">
          <NinxlScrollReveal>
            <NinxlSectionHeader
              label="ZERO TRUST"
              title="Hardware-native security"
              description="Dedicated AI execution layers that operate without public cloud dependency."
            />
          </NinxlScrollReveal>

          <NinxlStaggerReveal className="grid gap-4 md:grid-cols-2" stagger={0.1}>
            {HARDWARE_FEATURES.map((item) => (
              <NinxlStaggerItem key={item.title}>
                <div className="ninxl-value-card group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 transition duration-300 hover:-translate-y-1 hover:border-[#21728d]/30 hover:shadow-xl hover:shadow-[#21728d]/10">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/8 to-[#21728d]/6 opacity-70 pointer-events-none" />
                  <div className="ninxl-card-glint" />
                  <div className="relative">
                    <div className="mb-4 inline-flex rounded-2xl border border-[#21728d]/15 bg-white/80 p-2.5 text-[#21728d]">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <div className="mb-3">
                      <span className="text-2xl font-semibold tracking-tight text-[#134b5f]">{item.stat}</span>
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-[#88a1ac]">{item.statLabel}</span>
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-slate-900">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{item.desc}</p>
                  </div>
                </div>
              </NinxlStaggerItem>
            ))}
          </NinxlStaggerReveal>
        </div>
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      {/* GOVERNANCE */}
      <Ninxl8BitSection alt data-section="governance" className="relative overflow-hidden">
        <NinxlEnterpriseSectionBackdrop variant="governance" />
        <div className="mx-auto max-w-5xl px-6">
          <NinxlScrollReveal>
            <NinxlSectionHeader
              label="GOVERNED PLATFORM"
              title="Built for governance"
              description="Team-wide deployment with permissions, audit trails, and enforceable AI policy."
            />
          </NinxlScrollReveal>

          <NinxlStaggerReveal className="grid gap-4 sm:grid-cols-2" stagger={0.08}>
            {GOVERNANCE_FEATURES.map((item) => (
              <NinxlStaggerItem key={item.title}>
                <div className="ninxl-hud-panel flex gap-4 p-5 transition duration-300 hover:border-[#21728d]/30">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#21728d]/10 text-[#21728d]">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="mb-1 font-semibold text-slate-900">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </div>
              </NinxlStaggerItem>
            ))}
          </NinxlStaggerReveal>
        </div>
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      {/* INDUSTRIES */}
      <Ninxl8BitSection data-section="industries" className="relative overflow-hidden">
        <NinxlEnterpriseSectionBackdrop variant="industries" />
        <div className="mx-auto max-w-5xl px-6">
          <NinxlScrollReveal>
            <NinxlSectionHeader
              label="REGULATED SECTORS"
              title="Trusted by regulated industries"
              description="Built for sectors where data sovereignty is a legal requirement."
            />
          </NinxlScrollReveal>

          <NinxlStaggerReveal className="grid gap-4 sm:grid-cols-2" stagger={0.08}>
            {REGULATED_INDUSTRIES.map((item) => (
              <NinxlStaggerItem key={item.title}>
                <div className={`ninxl-enterprise-card rounded-3xl border p-5 transition duration-300 hover:-translate-y-1 hover:shadow-lg ${item.accent}`}>
                  <div className="ninxl-enterprise-icon mb-3 inline-flex rounded-xl p-2.5">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <h3 className="ninxl-theme-text-accent mb-2 font-bold">{item.title}</h3>
                  <p className="ninxl-theme-text-muted text-sm">{item.desc}</p>
                </div>
              </NinxlStaggerItem>
            ))}
          </NinxlStaggerReveal>
        </div>
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      {/* CONNECT PLATFORM */}
      <Ninxl8BitSection id="connect-platform" data-section="connect-platform" className="scroll-mt-24">
        <div className="mx-auto max-w-6xl px-6">
          <NinConnectPlatformSection variant="enterprise" />
        </div>
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      {/* CTA */}
      <Ninxl8BitSection alt className="py-24 text-center" data-section="contact">
        <NinxlScrollReveal variant="scale">
          <Ninxl8BitFrame className="mx-auto max-w-2xl">
            <div className="ninxl-8bit-label mb-4 text-center">GET STARTED</div>
            <h2 className="ninxl-8bit-heading ninxl-theme-text mb-4 text-base md:text-lg">
              Accelerate your organization&apos;s AI maturity
            </h2>
            <p className="ninxl-8bit-body mb-8 text-center">
              A multi-layered commercial model tailored to sovereign infrastructure needs.
            </p>

            <div className="mb-8 grid gap-3 text-left sm:grid-cols-3">
              {COMMERCIAL_MODEL.map((item, index) => (
                <div key={item.title} className="ninxl-hud-panel p-4">
                  <div className="ninxl-theme-text-accent mb-1 text-xs font-bold">0{index + 1}</div>
                  <div className="mb-1 text-sm font-semibold text-slate-900">{item.title}</div>
                  <p className="text-xs text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>

            <p className="ninxl-8bit-body mb-6">
              Contact our sales team to schedule a pilot and reclaim your data sovereignty.
            </p>
            <Link
              href={`mailto:${NINX_ENTERPRISE_SALES_EMAIL}?subject=NINX%20Enterprise%20Pilot%20Inquiry`}
              className="ninxl-pixel-btn ninxl-pixel-btn-primary inline-block"
            >
              Contact Sales
            </Link>
            <div className="ninxl-8bit-label mt-4 flex items-center justify-center gap-2">
              <FileSearch className="h-3.5 w-3.5" />
              Pilot programs available for regulated teams
            </div>
          </Ninxl8BitFrame>
        </NinxlScrollReveal>
      </Ninxl8BitSection>
    </>
  )
})